import axios from 'axios';
import localforage from 'localforage';

// Create React App exposes variables prefixed with REACT_APP_.  If this project
// is later migrated to Vite, replace this with import.meta.env.VITE_API_BASE_URL.
const BASE_URL = (process.env.REACT_APP_API_BASE_URL || 'https://utkarsh-abhyasika-backend.onrender.com').replace(/\/$/, '');

// Student records may return an API-relative image path (for example,
// /uploads/student.jpg). Image elements need the backend origin prefixed.
export const resolveApiUrl = (value) => {
  if (!value || typeof value !== 'string') return null;
  if (/^(https?:|blob:|data:)/i.test(value)) return value;
  return `${BASE_URL}/${value.replace(/^\/+/, '')}`;
};

// 1. Create the Axios instance (Equivalent to OkHttpClient.Builder)
export const apiClient = axios.create({
  baseURL: BASE_URL,
  timeout: 60000,
});

// The backend can receive the current student's id consistently, including on
// requests where it is not part of the URL or JSON body.
apiClient.interceptors.request.use(async (config) => {
  const [studentId, studentToken] = await Promise.all([
    localforage.getItem('studentId'),
    localforage.getItem('studentToken'),
  ]);
  if (studentId) {
    config.headers['x-student-id'] = studentId;
    config.headers.studentId = studentId;
  }
  if (studentToken) {
    config.headers.Authorization = `Bearer ${studentToken}`;
  }
  return config;
});

// 2. The API Service (Equivalent to your Retrofit Interface)
export const ApiService = {
  
  BASE_URL: BASE_URL, // Exported so Dashboard PDF links can use it

  // -------- AUTH & REGISTRATION --------
  
  login: (data) => apiClient.post('/api/login', data),
  
  logout: (data) => apiClient.post('/api/logout', data),
  
  sendOtp: (data) => apiClient.post('/api/registration-send-otp', data),
  
  verifyOtp: (data) => apiClient.post('/api/registration-verify-otp', data),
  
  signVerifyOtp: (data) => apiClient.post('/api/verify-otp', data),
  
  changePassword: (data) => apiClient.post('/api/change-password', data),
  
  // Note: For multipart/form-data, we pass the FormData object directly 
  // and set the specific headers required by the browser.

  
  registerWithPhoto: (formData) => {
    return apiClient.post('/register-with-photo', formData);
  },


  // -------- STUDENT DATA --------
  
  getStudent: (id) => apiClient.get(`/api/student/${id}`),

  getStudentPhoto: (studentId) => apiClient.get(`/api/student-photo/${studentId}`),
  
  updateProfile: (formData) => apiClient.post('/api/update-profile', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),

  // -------- NOTICES --------
  
  getLatestNotice: () => apiClient.get('/api/notice/latest'),
  
  getAllNotices: () => apiClient.get('/api/notice/all'),
  
  getStudentNotices: (studentId) => apiClient.get(`/api/notice/all/${studentId}`),
  
  getUnreadNoticeCount: (studentId) => apiClient.get(`/api/notice/unread-count/${studentId}`),
  
  markNoticeRead: (data) => apiClient.post('/api/notice/read', data),

  // -------- PAYMENTS & BILLING --------
  
  getPaymentStatus: (studentId) => apiClient.get(`/api/payment/status/${studentId}`),
  
  getPaymentHistory: (studentId) => apiClient.get(`/api/payment/history/${studentId}`),
  
  uploadPaymentProof: (formData) => apiClient.post('/api/admin-app/ocr', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),

  // -------- VOTING --------
  
  getLatestVote: (studentId) => apiClient.get(`/api/vote/latest/${studentId}`),
  
  respondVote: (data) => apiClient.post('/api/vote/respond', data),

  // -------- SUPPORT & FEEDBACK --------
  
  sendSupportMsg: (data) => apiClient.post('/api/support/send', data),
  
  getSupportHistory: (studentId) => apiClient.get(`/api/support/history/${studentId}`),
  
  sendFeedback: (data) => apiClient.post('/api/feedback/send', data),
  
  getFeedbackHistory: (studentId) => apiClient.get(`/api/feedback/history/${studentId}`),

  // -------- MISC --------
  
  saveFcmToken: (data) => apiClient.post('/api/fcm/save', data),
  
  getSeatStats: () => apiClient.get('/api/seat-stats')
};
